/** Performance configuration for Mapbox components Centralises all optimisation settings. */

export interface MapboxPerformanceConfig {
  map: {
    antialias: boolean;
    performanceMetricsCollection: boolean;
    renderWorldCopies: boolean;
    maxTileCacheSize: number;
    localIdeographFontFamily: string;
  };
  rendering: {
    maxMarkersBeforeVirtualization: number;
    maxRoutesBeforeSimplification: number;
    coordinatePrecision: number;
    simplificationTolerance: number;
  };
  caching: {
    maxCacheSize: number;
    cacheExpirationMs: number;
    geometrySimplificationCache: boolean;
    popupContentCache: boolean;
  };
  events: {
    debounceDelays: {
      resize: number;
      filter: number;
      sourceUpdate: number;
      centerOnContents: number;
    };
    throttleDelays: {
      mousemove: number;
      scroll: number;
      zoom: number;
    };
  };
  memory: {
    enableGarbageCollection: boolean;
    cleanupInterval: number;
    maxEventListeners: number;
  };
}

export const MAPBOX_PERFORMANCE_CONFIG: MapboxPerformanceConfig = {
  map: {
    antialias: false,
    performanceMetricsCollection: false,
    renderWorldCopies: false,
    maxTileCacheSize: 100,
    localIdeographFontFamily: "'Noto Sans', 'Noto Sans CJK SC', sans-serif"
  },
  rendering: {
    maxMarkersBeforeVirtualization: 1000,
    maxRoutesBeforeSimplification: 100,
    coordinatePrecision: 6,
    simplificationTolerance: 0.001
  },
  caching: {
    maxCacheSize: 100,
    cacheExpirationMs: 5 * 60 * 1000,
    geometrySimplificationCache: true,
    popupContentCache: true
  },
  events: {
    debounceDelays: {
      resize: 250,
      filter: 200,
      sourceUpdate: 150,
      centerOnContents: 300
    },
    throttleDelays: {
      mousemove: 16,
      scroll: 16,
      zoom: 100
    }
  },
  memory: {
    enableGarbageCollection: true,
    cleanupInterval: 60000,
    maxEventListeners: 50
  }
};

export const MAPBOX_LOW_RESOURCE_CONFIG: MapboxPerformanceConfig = {
  ...MAPBOX_PERFORMANCE_CONFIG,
  rendering: {
    ...MAPBOX_PERFORMANCE_CONFIG.rendering,
    maxMarkersBeforeVirtualization: 500,
    maxRoutesBeforeSimplification: 50,
    coordinatePrecision: 4,
    simplificationTolerance: 0.005
  },
  caching: {
    ...MAPBOX_PERFORMANCE_CONFIG.caching,
    maxCacheSize: 50,
    cacheExpirationMs: 3 * 60 * 1000
  },
  events: {
    debounceDelays: {
      resize: 500,
      filter: 400,
      sourceUpdate: 300,
      centerOnContents: 500
    },
    throttleDelays: {
      mousemove: 33,
      scroll: 33,
      zoom: 200
    }
  }
};

export function getOptimalPerformanceConfig(): MapboxPerformanceConfig {
  const isLowResource = detectLowResourceEnvironment();
  return isLowResource ? MAPBOX_LOW_RESOURCE_CONFIG : MAPBOX_PERFORMANCE_CONFIG;
}

function detectLowResourceEnvironment(): boolean {
  if ('memory' in performance) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const memory = performance.memory as any;
    const totalMemory = memory.totalJSHeapSize;

    if (totalMemory < 100 * 1024 * 1024) {
      return true;
    }
  }

  if ('hardwareConcurrency' in navigator) {
    if (navigator.hardwareConcurrency <= 2) {
      return true;
    }
  }

  if ('connection' in navigator) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const connection = (navigator as any).connection;
    if (
      connection &&
      (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g')
    ) {
      return true;
    }
  }

  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

export function applyPerformanceConfig(
  mapOptions: mapboxgl.MapboxOptions,
  config: MapboxPerformanceConfig = getOptimalPerformanceConfig()
): mapboxgl.MapboxOptions {
  return {
    ...mapOptions,
    antialias: config.map.antialias,
    performanceMetricsCollection: config.map.performanceMetricsCollection,
    renderWorldCopies: config.map.renderWorldCopies,
    maxTileCacheSize: config.map.maxTileCacheSize,
    localIdeographFontFamily: config.map.localIdeographFontFamily
  };
}
